import { useDeliveryBarErrorsWithContext } from '@features/DeliveryBar/useDeliveryBarErrors';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import {
  DeliveryBarMachineContext,
  useEligibility,
  useGrowthBook,
} from '@monofront/business/contexts';
import { EVENTS, STATES } from '@monofront/business/stateMachines/deliveryBar';
import { getIsFresh, getIsUltraFresh } from '@monofront/business/utils';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import Modal from '@monofront/design-system/organisms/Modal';
import { IShoppingCartProduct, ShippingMethodTypes } from '@monofront/types';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import SubscribeToWaitingList from './SubscribeToWaitingList';
import { useLocalOrRemoteCustomer } from '@hooks/useLocalOrRemoteCustomer';
import { usePreferredShippingMethodWithConfigurations } from '@hooks/usePreferredShippingMethodWithConfiguration';
import { useDeliveryBar } from '@context/DeliveryBarContext';

const IMG_SIZE = 48;

type ProductFilter = (product: IShoppingCartProduct) => boolean;

const freshFilter: ProductFilter = (product: IShoppingCartProduct) =>
  getIsFresh(product.tags);
const ultraFreshFilter: ProductFilter = (product: IShoppingCartProduct) =>
  getIsUltraFresh(product.tags);

export const ErrorModalIneligibleProductsInCart =
  (
    translationKey:
      | 'DeliveryBar.Errors.INELIGIBLE_PRODUCTS_IN_CART'
      | 'DeliveryBar.Errors.INELIGIBLE_PRODUCT_ADDED_TO_CART'
      | 'DeliveryBar.Errors.INELIGIBLE_SHIPPING_METHOD_SELECTED',
    redirectToOnContinue?: string,
    withAlertButton = false,
  ) =>
  () => {
    const tError = useTranslations(translationKey);

    const growthbook = useGrowthBook();
    const { shoppingCart, shoppingCartId, deleteProductsFromShoppingCart } =
      useShoppingCart();
    const { waitingProduct, setWaitingProduct, setIsSidePanelOpen } =
      useDeliveryBar();
    const { error } = useDeliveryBarErrorsWithContext();
    const { checkAddressEligibility } = useEligibility();
    const [isSubscribeToWaitingListOpen, setIsSubscribeToWaitingListOpen] =
      useState(false);
    const { send } = DeliveryBarMachineContext.useActorRef();
    const { resolvedCustomer } = useLocalOrRemoteCustomer();

    const selectedShippingMethod =
      usePreferredShippingMethodWithConfigurations();
    const address = resolvedCustomer?.address ?? null;

    const ineligibleProductsFilters = useMemo(() => {
      const filters: ProductFilter[] = [];

      if (!error) return filters;

      if (selectedShippingMethod?.type === ShippingMethodTypes.PICKUP) {
        filters.push(freshFilter, ultraFreshFilter);
      } else {
        const { fresh: isFreshEligible, ultraFresh: isUltraFreshEligible } =
          checkAddressEligibility(growthbook, address);

        if (!isFreshEligible) filters.push(freshFilter);
        if (!isUltraFreshEligible) filters.push(ultraFreshFilter);
      }

      return filters;
    }, [error, address, selectedShippingMethod]);

    const ineligibleProducts = useMemo(() => {
      if (waitingProduct) return [waitingProduct];

      if (
        !shoppingCart.products?.length ||
        !error ||
        !ineligibleProductsFilters.length
      )
        return [];

      return shoppingCart.products.filter((product) =>
        ineligibleProductsFilters.some((filter) => filter(product)),
      );
    }, [error, shoppingCart, ineligibleProductsFilters]);

    const [errorDescription, errorTitle] = (() => {
      if (!error) return '';

      if (error.errorAtState === STATES.NEED_PICKUP_POINT_SELECTION)
        return [tError('PICKUP_NEEDED.message'), tError('title')];

      return [tError('SHIPPING_METHOD_SELECTED.message'), tError('title')];
    })();

    const handleCloseModal = () => {
      setWaitingProduct(undefined);
    };

    const handleGoBack = () => {
      handleCloseModal();
      send({ type: EVENTS.GO_TO_STATE_SHIPPING_METHOD_SELECTOR });
      setIsSidePanelOpen(true);
    };

    const handleContinue = async () => {
      await deleteProductsFromShoppingCart(shoppingCartId, ineligibleProducts);
    };

    return (
      <>
        <Modal
          isOpen={!!error}
          externalToggle={handleCloseModal}
          centered
          UNSAFE_className="tw-px-10 tw-py-8 tw-w-[390px] tw-max-h-[550px]"
        >
          <h2 className="tw-lf-title-sm tw-mb-3 tw-text-center">
            {errorTitle}
          </h2>
          {errorDescription && (
            <p className="tw-lf-body-regular-md tw-text-center">
              {errorDescription}
            </p>
          )}
          <div className="tw-flex tw-justify-center">
            <div className="tw-my-6 tw-inline-flex tw-max-h-56 tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 tw-overflow-y-auto tw-rounded-lg tw-bg-[#FFE7DA80] tw-p-3">
              {ineligibleProducts.map((product) => {
                if (!product.image) return null;

                return (
                  <div key={product.sku}>
                    <ResponsiveImage
                      src={product.image}
                      width={IMG_SIZE}
                      height={IMG_SIZE}
                      alt={product.title}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          {redirectToOnContinue ? (
            <Link
              className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-mx-auto tw-mb-3"
              href={redirectToOnContinue}
              onClick={() => {
                setWaitingProduct(undefined);
              }}
            >
              {tError('continueButton')}
            </Link>
          ) : (
            <button
              className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-mx-auto tw-mb-3"
              onClick={handleContinue}
            >
              {tError('continueButton')}
            </button>
          )}
          {withAlertButton ? (
            <button
              onClick={() => {
                setIsSubscribeToWaitingListOpen(true);
              }}
              className="tw-lf-button tw-lf-button-secondary tw-lf-button-md tw-mx-auto tw-mb-4"
            >
              {tError('alertButton')}
            </button>
          ) : (
            <button
              onClick={handleGoBack}
              className="tw-lf-button tw-lf-button-secondary tw-lf-button-md tw-mx-auto tw-mb-4"
            >
              {tError('goBackButton')}
            </button>
          )}
        </Modal>
        <SubscribeToWaitingList
          isOpen={isSubscribeToWaitingListOpen}
          handleCloseModal={() => {
            setWaitingProduct(undefined);
            setIsSubscribeToWaitingListOpen(false);
          }}
        />
      </>
    );
  };
